import React, { useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
interface ForgotPasswordProps {
  onBack: () => void;
}
export function ForgotPassword({
  onBack
}: ForgotPasswordProps) {
  const [step, setStep] = useState<'email' | 'otp' | 'newPassword' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would send the OTP to the email
    setStep('otp');
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      // Auto-advance to next input
      if (value !== '' && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would verify the OTP
    setStep('newPassword');
  };
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would update the password
    setStep('success');
  };
  // Password validation
  const isPasswordValid = newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword);
  const isFormValid = isPasswordValid && newPassword === confirmPassword;
  return <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100" data-id="element-6">
      <div className="flex items-center mb-6" data-id="element-7">
        <button onClick={onBack} className="mr-3 text-gray-500 hover:text-gray-700" aria-label="Go back" data-id="element-8">
          <ArrowLeftIcon size={20} data-id="element-9" />
        </button>
        <h2 className="text-2xl font-semibold text-[#1A1F36]" data-id="element-10">
          {step === 'email' && 'Forgot Password'}
          {step === 'otp' && 'Enter OTP'}
          {step === 'newPassword' && 'Reset Password'}
          {step === 'success' && 'Success'}
        </h2>
      </div>
      {step === 'email' && <form onSubmit={handleEmailSubmit} className="space-y-6" data-id="element-11">
          <div className="space-y-2" data-id="element-12">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-13">
              Official Email
            </label>
            <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter your official email" required data-id="element-14" />
          </div>
          <button type="submit" disabled={!email.includes('@')} className={`w-full py-2 px-4 rounded-md font-medium text-white ${email.includes('@') ? 'bg-[#F58220] hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-15">
            Send OTP
          </button>
        </form>}
      {step === 'otp' && <form onSubmit={handleOtpSubmit} className="space-y-6" data-id="element-16">
          <p className="text-sm text-gray-600 mb-4" data-id="element-17">
            We've sent a one-time password to your email. Please enter it below.
          </p>
          <div className="flex justify-between gap-2" data-id="element-18">
            {otp.map((digit, index) => <input key={index} id={`otp-${index}`} type="text" value={digit} onChange={e => handleOtpChange(index, e.target.value)} className="w-10 h-12 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" maxLength={1} pattern="[0-9]" inputMode="numeric" required data-id="element-19" />)}
          </div>
          <button type="submit" disabled={otp.some(digit => digit === '')} className={`w-full py-2 px-4 rounded-md font-medium text-white ${!otp.some(digit => digit === '') ? 'bg-[#F58220] hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-20">
            Verify OTP
          </button>
        </form>}
      {step === 'newPassword' && <form onSubmit={handlePasswordSubmit} className="space-y-6" data-id="element-21">
          <div className="space-y-2" data-id="element-22">
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700" data-id="element-23">
              New Password
            </label>
            <input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter new password" required data-id="element-24" />
            <div className="text-xs text-gray-600 mt-1" data-id="element-25">
              <p className="font-medium" data-id="element-26">Password must contain:</p>
              <ul className="list-disc pl-4 space-y-1 mt-1" data-id="element-27">
                <li className={newPassword.length >= 12 ? 'text-green-600' : ''} data-id="element-28">
                  Minimum 12 characters
                </li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''} data-id="element-29">
                  At least 1 uppercase letter
                </li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''} data-id="element-30">
                  At least 1 lowercase letter
                </li>
                <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''} data-id="element-31">
                  At least 1 number
                </li>
                <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : ''} data-id="element-32">
                  At least 1 special character
                </li>
              </ul>
            </div>
          </div>
          <div className="space-y-2" data-id="element-33">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700" data-id="element-34">
              Confirm Password
            </label>
            <input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Confirm new password" required data-id="element-35" />
            {newPassword && confirmPassword && newPassword !== confirmPassword && <p className="text-red-500 text-xs mt-1" data-id="element-36">
                  Passwords do not match
                </p>}
          </div>
          <button type="submit" disabled={!isFormValid} className={`w-full py-2 px-4 rounded-md font-medium text-white ${isFormValid ? 'bg-[#F58220] hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-37">
            Reset Password
          </button>
        </form>}
      {step === 'success' && <div className="text-center space-y-6" data-id="element-38">
          <div className="flex justify-center" data-id="element-39">
            <CheckCircleIcon size={64} className="text-green-500" data-id="element-40" />
          </div>
          <div data-id="element-41">
            <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-42">
              Password Reset Successful
            </h3>
            <p className="text-gray-600" data-id="element-43">
              Your password has been reset successfully.
            </p>
          </div>
          <button onClick={onBack} className="w-full py-2 px-4 bg-[#F58220] hover:bg-[#e07619] text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50" data-id="element-44">
            Back to Login
          </button>
        </div>}
    </div>;
}